import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { resourcesApi, apiPath, authTenantHeaders } from '../services/api';
import { Resource } from '../types';
import {
  Plus,
  Pencil,
  Trash2,
  Link,
  Play,
  Copy,
  Check,
  Box,
  Server,
  RotateCw,
} from 'lucide-react';
import { format } from 'date-fns';
import ResourceForm from '../components/ResourceForm';
import { DatabaseConnectionModal } from '../components/DatabaseConnectionModal';
import XTermSSHTerminal from '../components/XTermSSHTerminal';
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  ColumnDef,
} from '@tanstack/react-table';
import { useUser } from '../contexts/UserContext';
import { usePermissions } from '../hooks/usePermissions';
import { Button, Card, Chip, EmptyState, IconButton, SearchField, PaginationBar, TanstackTable, Modal, PageHeader, Select, StatusBadge } from '@/components/ds-zta';
import { useUrlState } from '@/lib/useUrlState';
import { ROWS_PER_PAGE_OPTIONS, readPage, readRows } from '@/lib/urlPaging';
import { healthRank, timeValue } from '@/lib/sortValues';
import { useDialogs } from '@/components/dialogs/DialogProvider';
import { toast } from '@/lib/toast';
import { errorMessage } from '@/lib/http';
import { copyWithToast } from '@/lib/copy';
import { ResourceTypeTile } from '@/lib/resourceIcons';
import { BRAND } from '@/brand';

const columnHelper = createColumnHelper<Resource>();

/** Formats a timestamp, or returns "—" when it is missing or unparseable
 *  (date-fns `format` throws "Invalid time value" on an Invalid Date). "—" is
 *  the one no-value token; "Never" means never-expires only (design eval F47). */
const formatLastSeen = (value: string | number | Date | null | undefined, pattern = 'MMM d, yyyy HH:mm') => {
  if (value === null || value === undefined || value === '') return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : format(d, pattern);
};

interface ResourcesTableProps {
  resources: Resource[];
  isLoading: boolean;
  getResourceTypeIcon: (type: string) => React.ReactNode;
  getEnvoyProxyUrl: (resource: Resource) => string | null;
  copyToClipboard: (text: string) => void;
  handleEdit: (resource: Resource) => void;
  handleDelete: (resource: Resource) => void;
  handleConnect: (resource: Resource) => void;
  deleteMutationLoading: boolean;
  canManage: boolean;
  searchTerm: string;
  onCreate: () => void;
}

const ResourcesTable: React.FC<ResourcesTableProps> = ({
  resources,
  isLoading,
  getResourceTypeIcon,
  getEnvoyProxyUrl,
  copyToClipboard,
  handleEdit,
  handleDelete,
  handleConnect,
  deleteMutationLoading,
  canManage,
  searchTerm,
  onCreate,
}) => {
  const columns = useMemo<ColumnDef<Resource, any>[]>(
    () => [
      columnHelper.display({
        id: 'resource',
        header: 'Resource',
        meta: { sortable: true, sortValue: (r: Resource) => (r.external_id || '').toLowerCase() },
        cell: ({ row }) => (
          <div className="flex items-center">
            <span className="mr-3">{getResourceTypeIcon(row.original.type)}</span>
            <div>
              <div className="text-dense font-medium text-[var(--d2d-text-primary)] font-mono tabular-nums">
                {row.original.external_id}
              </div>
              <div className="text-dense text-[var(--d2d-text-muted)]">
                ID: {row.original.id.slice(0, 8)}...
              </div>
            </div>
          </div>
        ),
      }),
      columnHelper.accessor('type', {
        header: 'Type',
        meta: { sortable: true },
        cell: (info) => (
          <Chip size="sm" variant="neutral" className="capitalize">
            {info.getValue()}
          </Chip>
        ),
      }),
      columnHelper.accessor('health_status', {
        header: 'Health',
        meta: { sortable: true, sortValue: (r: Resource) => healthRank(r.health_status) },
        cell: (info) => {
          const status = info.getValue();
          return status ? (
            <StatusBadge value={status} className="capitalize">{status}</StatusBadge>
          ) : null;
        },
      }),
      columnHelper.accessor('last_seen', {
        header: 'Last Seen',
        meta: { sortable: true, sortValue: (r: Resource) => timeValue(r.last_seen) },
        cell: (info) => (
          <span className="text-dense text-[var(--d2d-text-muted)] text-nowrap">
            {formatLastSeen(info.getValue())}
          </span>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: () => <span className="block text-right">Actions</span>,
        cell: ({ row }) => {
          // Show Connect button for connectable resource types (database, ssh, k8s)
          const isConnectable = ['database', 'ssh', 'k8s'].includes(row.original.type);

          return (
            <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
              {isConnectable && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleConnect(row.original)}
                  title="View connection details and CLI commands"
                  leadingIcon={<Play className="h-4 w-4" />}
                >
                  Connect
                </Button>
              )}
              {canManage && (
                <>
                  <IconButton
                    size="sm"
                    aria-label="Edit resource"
                    tooltip="Edit resource"
                    icon={<Pencil className="h-4 w-4" />}
                    onClick={() => handleEdit(row.original)}
                  />
                  <IconButton
                    size="sm"
                    aria-label="Delete resource"
                    tooltip="Delete resource"
                    icon={<Trash2 className="h-4 w-4" />}
                    onClick={() => handleDelete(row.original)}
                    disabled={deleteMutationLoading}
                  />
                </>
              )}
            </div>
          );
        },
      }),
    ],
    [getResourceTypeIcon, getEnvoyProxyUrl, copyToClipboard, handleEdit, handleDelete, handleConnect, deleteMutationLoading, canManage]
  );

  const table = useReactTable({
    data: resources,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const empty = searchTerm ? (
    <EmptyState
      icon={<Server />}
      title={`No resources on this page match "${searchTerm}"`}
      description="The search covers this page. Try another page, a different search, or clear the filters."
    />
  ) : (
    <EmptyState
      icon={<Server />}
      title="No resources yet"
      description="Registered resources appear here once they are added."
      action={
        canManage ? (
          <Button onClick={onCreate} leadingIcon={<Plus className="h-4 w-4" />}>
            Add Resource
          </Button>
        ) : undefined
      }
    />
  );

  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <TanstackTable aria-label="Resources" table={table} loading={isLoading} empty={empty} />
      </div>
    </Card>
  );
};

export default function Resources() {
  // Filters, search and paging live in the URL (design eval F24). Role and type
  // go to the server, so changing either starts again at page 1 (F7). The
  // list endpoint takes no search param, so the search filters the loaded
  // page and says so.
  const [url, setUrl] = useUrlState({ q: '', role: '', type: '', page: '1', rows: '20' });
  const searchTerm = url.q;
  const setSearchTerm = (next: string) => setUrl({ q: next }, { replace: true });
  const selectedRole = url.role;
  const setSelectedRole = (next: string) => setUrl({ role: next, page: null });
  const selectedType = url.type;
  const setSelectedType = (next: string) => setUrl({ type: next, page: null });
  const page = readPage(url.page) - 1; // zero-based below, as before
  const itemsPerPage = readRows(url.rows);
  const setPage = (next: number) => setUrl({ page: next + 1 });
  const [showForm, setShowForm] = useState(false);
  const [editingResource, setEditingResource] = useState<Resource | null>(null);

  const queryClient = useQueryClient();
  const { secondaryRoles } = useUser();
  const { canManageResources } = usePermissions();
  const { confirm } = useDialogs();

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    ['resources', selectedType, selectedRole, page, itemsPerPage],
    () => resourcesApi.listByRole(selectedType || undefined, itemsPerPage, page * itemsPerPage, selectedRole || undefined),
    { keepPreviousData: true }
  );

  const deleteMutation = useMutation(resourcesApi.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('resources');
      toast.success('Resource deleted');
    },
    onError: (err) => {
      toast.error(errorMessage(err, 'Failed to delete resource'));
    },
  });

  const handleDelete = async (resource: Resource) => {
    if (await confirm({
      title: 'Delete resource?',
      message: `Delete ${resource.type} resource "${resource.external_id}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      variant: 'danger',
    })) {
      deleteMutation.mutate(resource.id);
    }
  };

  const handleEdit = (resource: Resource) => {
    setEditingResource(resource);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingResource(null);
  };

  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [showSSHModal, setShowSSHModal] = useState(false);
  const [showK8sModal, setShowK8sModal] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState<any>(null);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [copiedCommand, setCopiedCommand] = useState<string | null>(null);

  const handleConnect = async (resource: Resource) => {
    try {
      // For database resources, use the existing backend endpoint
      if (resource.type === 'database') {
        const response = await fetch(apiPath(`/api/v1/database/${resource.id}/connect`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authTenantHeaders(),
          },
          credentials: 'include',
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || errorData.error || 'Failed to get connection info');
        }

        const data = await response.json();
        setConnectionInfo(data);
        setSelectedResource(resource);
        setShowConnectionModal(true);
      } else if (resource.type === 'ssh') {
        // For SSH resources, use the dedicated SSH modal
        setSelectedResource(resource);
        setShowSSHModal(true);
      } else if (resource.type === 'k8s') {
        // For K8s resources, use the K8s CLI Commands modal
        setSelectedResource(resource);
        setShowK8sModal(true);
      } else {
        // For SSH and K8s, generate CLI instructions client-side
        const resourceName = resource.external_id;
        const resourceType: string = resource.type;

        // Get command prefix based on resource type
        const cmdPrefix = resourceType === 'k8s' ? 'k8s' : resourceType;

        // Generate connection info for SSH/K8s
        const generatedInfo = {
          status: 'success',
          access_token: '', // Not needed for client-side display
          expires_at: new Date(Date.now() + 3600000).toISOString(), // Placeholder
          instructions: {
            step_1: {
              command: 'iap auth login',
              description: `Authenticate with ${BRAND.name} (if not already logged in)`,
            },
            step_2: {
              command: `iap ${cmdPrefix} connect ${resourceName}`,
              description: `Create secure tunnel to ${resourceType.toUpperCase()} resource`,
            },
          },
          connection_details: {
            host: 'localhost',
            port: resourceType === 'k8s' ? '6443' : '2222',
            protocol: resourceType === 'k8s' ? 'https' : 'ssh',
            database: resourceName, // Use resource name as identifier
          },
          notes: [
            `Run 'iap ${cmdPrefix} ls' to list all available ${resourceType.toUpperCase()} resources`,
            'The tunnel will run in the background on your local machine',
            resourceType === 'ssh'
              ? `Connect with: ssh -p 2222 <user>@localhost`
              : `Use kubectl with: kubectl --server=https://localhost:6443 get pods`,
            `Use 'iap ${cmdPrefix} disconnect ${resourceName}' to close the tunnel`,
          ],
        };

        setConnectionInfo(generatedInfo);
        setSelectedResource(resource);
        setShowConnectionModal(true);
      }
    } catch (error: any) {
      console.error('Failed to get connection info:', error);

      // Parse the error message to provide more helpful feedback
      const errorMessage = error.message || 'Failed to get connection information';

      if (errorMessage.includes('pending')) {
        toast.error(<span className="whitespace-pre-line">{`⏳ Access Pending\n\nYour access request is pending approval.\nPlease wait for an administrator to approve your request.`}</span>);
      } else if (errorMessage.includes('expired')) {
        toast.error(<span className="whitespace-pre-line">{`⏰ Access Expired\n\nYour access to this resource has expired.\nPlease submit a new access request from the Access Requests page.`}</span>);
      } else if (errorMessage.includes('no valid access') || errorMessage.includes('Access denied')) {
        toast.error(<span className="whitespace-pre-line">{`Access Required\n\n${errorMessage}\n\nTo access this resource:\n1. Go to Access Requests in the menu\n2. Submit a new access request for this resource\n3. Wait for administrator approval`}</span>);
      } else {
        toast.error(<span className="whitespace-pre-line">{errorMessage}</span>);
      }
    }
  };

  const filteredResources = data?.data.resources?.filter(resource =>
    resource.external_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    resource.type.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const getResourceTypeIcon = (type: string) => <ResourceTypeTile type={type} />;

  const getEnvoyProxyUrl = (resource: Resource) => {
    if (resource.type === 'http_proxy') {
      // Generate proxy URL based on Envoy routing patterns
      // For web apps, use /services/{service-name} pattern
      // For APIs, use /api/ pattern

      const resourceId = resource.external_id;
      let proxyPath = '';

      // Determine the correct Envoy route based on resource type and name
      if (resourceId.includes('web-app') || resourceId.includes('webapp')) {
        proxyPath = '/services/web-app';
      } else if (resourceId.includes('admin')) {
        proxyPath = '/services/admin';
      } else if (resourceId.includes('api')) {
        proxyPath = '/api/';
      } else {
        // Default to services pattern for HTTP resources
        const serviceName = resourceId.replace(/[^a-zA-Z0-9-]/g, '-').toLowerCase();
        proxyPath = `/services/${serviceName}`;
      }

      const proxyUrl = `${window.location.origin}${proxyPath}`;
      console.log('Resource ID:', resourceId);
      console.log('Original endpoint:', resource.endpoint.url);
      console.log('Generated proxy URL:', proxyUrl);
      return proxyUrl;
    }
    return null;
  };

  const copyToClipboard = (text: string) => { void copyWithToast(text); };

  return (
    <div className="animate-fade-in">
      <div data-tour="database-header" className="mb-6">
        <PageHeader
          title="Resources"
          icon={<Server />}
          description="Manage your registered resources and their access policies"
          onRefresh={() => refetch()}
          refreshing={isFetching}
          primaryAction={
            canManageResources() ? (
              <Button
                data-tour="database-add"
                onClick={() => setShowForm(true)}
                leadingIcon={<Plus className="h-4 w-4" />}
              >
                Add Resource
              </Button>
            ) : undefined
          }
        />
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6 overflow-visible" data-tour="database-filter">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <SearchField
              label="Search resources on this page"
              placeholder="Search resources on this page..."
              value={searchTerm}
              onChange={(value) => setSearchTerm(value)}
              help={(data?.data.total ?? 0) > (data?.data.resources?.length ?? 0) ? `Searches the ${(data?.data.resources?.length ?? 0)} shown on this page, of ${(data?.data.total ?? 0)}.` : undefined}
            />
          </div>
          {secondaryRoles.length > 0 && <div className="sm:w-48">
            <Select
              className="gap-0" /* hidden label: no gap above the field, so it lines up with the search (F46) */
              label={<span className="sr-only">Select Role</span>}
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
            >
              <option value="">Select Role</option>
              {secondaryRoles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </Select>
          </div>}
          <div data-tour="database-type" className="sm:w-48">
            <Select
              className="gap-0" /* hidden label: no gap above the field, so it lines up with the search (F46) */
              label={<span className="sr-only">Resource type</span>}
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
            >
              <option value="">All Types</option>
              <option value="database">Database</option>
              <option value="http_proxy">HTTP Proxy</option>
              <option value="ssh">SSH Server</option>
              <option value="k8s">Kubernetes</option>
            </Select>
          </div>
        </div>
      </Card>

      {/* Resources Table */}
      <div data-tour="database-list">
        {error ? (
          <Card className="overflow-hidden">
            <EmptyState
              icon={<Server />}
              title="Failed to load resources"
              description="Something went wrong while loading resources."
              action={
                <Button variant="secondary" onClick={() => refetch()} leadingIcon={<RotateCw className="h-4 w-4" />}>
                  Retry
                </Button>
              }
            />
          </Card>
        ) : (
        <ResourcesTable
          resources={filteredResources}
          isLoading={isLoading}
          getResourceTypeIcon={getResourceTypeIcon}
          getEnvoyProxyUrl={getEnvoyProxyUrl}
          copyToClipboard={copyToClipboard}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          handleConnect={handleConnect}
          deleteMutationLoading={deleteMutation.isLoading}
          canManage={canManageResources()}
          searchTerm={searchTerm}
          onCreate={() => setShowForm(true)}
        />
        )}
      </div>

      {/* Pagination */}
      {data && data.data.total > 0 && (
        <div className="mt-4">
          {data.data.total > 0 && <PaginationBar
            page={page + 1}
            totalRows={data.data.total}
            rowsPerPage={itemsPerPage}
            onPageChange={(p) => setPage(p - 1)}
            onRowsPerPageChange={(newItemsPerPage) => setUrl({ rows: newItemsPerPage, page: null })}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />}
        </div>
      )}

      {/* Resource Form Modal */}
      {showForm && (
        <ResourceForm
          resource={editingResource}
          onClose={handleFormClose}
          onSuccess={() => {
            queryClient.invalidateQueries('resources');
            handleFormClose();
          }}
        />
      )}

      {/* Database Connection Modal */}
      {showConnectionModal && connectionInfo && selectedResource && (
        <DatabaseConnectionModal
          isOpen={showConnectionModal}
          onClose={() => {
            setShowConnectionModal(false);
            setConnectionInfo(null);
            setSelectedResource(null);
          }}
          connectionInfo={connectionInfo}
          resourceName={selectedResource.external_id}
        />
      )}

      {/* K8s CLI Commands Modal */}
      {showK8sModal && selectedResource && (() => {
        const clusterName = selectedResource.endpoint?.cluster_name || selectedResource.external_id;
        const commands = [
          { title: `Authenticate with ${BRAND.name}`, description: `Log in to ${BRAND.name} using OIDC authentication`, command: 'iap auth login' },
          { title: 'List available clusters', description: 'List all Kubernetes clusters you have access to', command: 'iap k8s ls' },
          { title: 'Connect to cluster', description: 'Start localhost tunnel to Kubernetes API server', command: `iap k8s connect ${clusterName}` },
          { title: 'Connect with custom port', description: 'Connect with a specific local port', command: `iap k8s connect ${clusterName} --local-port 6444` },
          { title: 'Connect in background', description: 'Run tunnel in background mode', command: `iap k8s connect ${clusterName} --background` },
          { title: 'Show active tunnels', description: 'Display all currently active Kubernetes API tunnels', command: 'iap k8s status' },
          { title: 'Close tunnel', description: 'Close an active tunnel to the cluster', command: `iap k8s logout ${clusterName}` },
        ];
        const kubectlCommands = [
          { title: 'Get pods', description: 'List pods using the generated kubeconfig', command: `kubectl --kubeconfig ~/.iap/k8s/${clusterName}.yaml get pods` },
          { title: 'Get namespaces', description: 'List all namespaces', command: `kubectl --kubeconfig ~/.iap/k8s/${clusterName}.yaml get namespaces` },
          { title: 'Set KUBECONFIG', description: 'Export KUBECONFIG environment variable', command: `export KUBECONFIG=~/.iap/k8s/${clusterName}.yaml` },
        ];
        const copyCmd = async (command: string) => {
          if (!(await copyWithToast(command, { success: false }))) return;
          setCopiedCommand(command);
          setTimeout(() => setCopiedCommand(null), 2000);
        };
        const renderCmdBlock = (cmd: { title: string; description: string; command: string }, index: number) => (
          <div key={index} className="bg-[var(--d2d-bg-primary)] rounded-lg p-3 border border-[var(--d2d-border)]">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h5 className="text-sm font-medium text-[var(--d2d-text-primary)]">{cmd.title}</h5>
                <p className="text-xs text-[var(--d2d-text-muted)] mt-0.5">{cmd.description}</p>
              </div>
              <Button
                variant={copiedCommand === cmd.command ? 'ghost' : 'secondary'}
                size="sm"
                onClick={() => copyCmd(cmd.command)}
                leadingIcon={copiedCommand === cmd.command ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              >
                {copiedCommand === cmd.command ? 'Copied!' : 'Copy'}
              </Button>
            </div>
            <pre className="text-sm text-[var(--d2d-text-primary)] bg-[var(--ds-color-bg-subtle)] p-2 rounded overflow-x-auto font-mono">{cmd.command}</pre>
          </div>
        );
        return (
          <Modal
            dismissOnOverlay={false}
            open
            onClose={() => { setShowK8sModal(false); setSelectedResource(null); }}
            title="K8s CLI Commands"
            description={<>Cluster: <span className="font-medium text-[var(--d2d-text-secondary)]">{clusterName}</span></>}
            size="lg"
            footer={
              <Button variant="secondary" onClick={() => { setShowK8sModal(false); setSelectedResource(null); }}>Close</Button>
            }
          >
              <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
                <div>
                  <h4 className="text-sm font-medium text-[var(--d2d-text-primary)] mb-3 flex items-center">
                    <Box className="h-4 w-4 mr-2 text-[var(--d2d-info)]" /> {BRAND.name} K8s Commands
                  </h4>
                  <div className="space-y-3">{commands.map(renderCmdBlock)}</div>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-[var(--d2d-text-primary)] mb-3 flex items-center">
                    <Play className="h-4 w-4 mr-2 text-[var(--d2d-success)]" /> After Tunnel Starts - kubectl Commands
                  </h4>
                  <div className="space-y-3">{kubectlCommands.map(renderCmdBlock)}</div>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-[var(--d2d-border)]">
                <div className="bg-[var(--zta-tint-info)] rounded-lg p-4">
                  <h4 className="text-sm font-medium text-[var(--d2d-info)] mb-2">Prerequisites</h4>
                  <ul className="text-xs text-[var(--d2d-info)] space-y-1 list-disc list-inside">
                    <li>Authenticate with {BRAND.name}: <code className="bg-[var(--zta-tint-info)] px-1 rounded">iap auth login</code></li>
                    <li>Ensure kubectl is installed for full functionality</li>
                  </ul>
                </div>
              </div>
          </Modal>
        );
      })()}

      {/* SSH Web Terminal */}
      {showSSHModal && selectedResource && (
        <XTermSSHTerminal
          isOpen={showSSHModal}
          onClose={() => {
            setShowSSHModal(false);
            setSelectedResource(null);
          }}
          resource={{
            id: selectedResource.id,
            external_id: selectedResource.external_id,
            name: selectedResource.external_id,
            endpoint: {
              host: selectedResource.endpoint?.host,
              port: selectedResource.endpoint?.port,
            },
          }}
          defaultUser={selectedResource.endpoint?.username || 'root'}
          enableRecording={false}
        />
      )}
    </div>
  );
}
